import { apiFetch, apiUrl, apiDownload, type IdResponse } from "@/lib/apiClient";
import { url } from "@/lib/basePath";
import { openBlobInNewTab } from "@/utils/download";
import { fetchAll, pageQuery, toPaged, type Paged, type PageParams } from "@/lib/paging";
import { odata } from "@/lib/odata";

export interface QuoteLineItem {
  id: string;

  productId: string | null;
  description: string;
  unitPrice: number;
  quantity: number;
  lineTotal: number;
  position: number;
}

export interface QuoteListItem {
  id: string;
  quoteNumber: number;
  quoteYear: number;
  invoiceNumber: number | null;
  invoiceYear: number | null;
  displayId: string;
  isInvoice: boolean;
  status: string;
  customerName: string;
  customerCompany: string | null;
  totalAmount: number;
  currency: string;
  ownerId: string;
  ownerName: string;
  createdAt: string;
  updatedAt: string;
}

export interface QuoteDetail {
  id: string;
  quoteNumber: number;
  quoteYear: number;
  invoiceNumber: number | null;
  invoiceYear: number | null;
  salesOrderId: string;
  invoiceId: string | null;
  displayId: string;
  isInvoice: boolean;
  status: string;
  customerName: string;
  customerCompany: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  customerAddress: string | null;
  shippingName: string | null;
  shippingCompany: string | null;
  shippingEmail: string | null;
  shippingPhone: string | null;
  shippingAddress: string | null;
  subtotal: number;
  discountPct: number;
  discountAmount: number;
  taxRate: number;
  taxAmount: number;
  shippingCharge: number;
  totalAmount: number;
  discountAppliesAfterTax: boolean;
  currency: string;
  notes: string | null;
  ownerId: string;
  ownerName: string;

  campaignId: string | null;
  campaignName: string | null;

  priceLevelSnap: number | null;
  sentAt: string | null;
  approvedAt: string | null;
  invoicedAt: string | null;
  paidAt: string | null;
  cancelledAt: string | null;
  createdAt: string;
  updatedAt: string;

  publicToken: string | null;
  items: QuoteLineItem[];
}

export interface QuoteItemInput {

  productId?: string | null;
  description: string;
  unitPrice: number;
  quantity: number;
}

export interface SaveQuoteRequest {
  saveAs?: "quote" | "invoice";
  customerName: string;
  customerCompany?: string | null;
  customerEmail?: string | null;
  customerPhone?: string | null;
  customerAddress?: string | null;
  shippingName?: string | null;
  shippingCompany?: string | null;
  shippingEmail?: string | null;
  shippingPhone?: string | null;
  shippingAddress?: string | null;
  discountPct?: number;
  discountAmount?: number;
  taxRate?: number;
  shippingCharge?: number;
  discountAppliesAfterTax?: boolean | null;
  notes?: string | null;

  campaignId?: string | null;
  items: QuoteItemInput[];
}

export type QuoteStatusAction = "Sent" | "Approved" | "Paid" | "Cancelled";

interface QuoteNumbering {
  quoteNumber: number;
  quoteYear: number;
  invoiceNumber: number | null;
  invoiceYear?: number | null;
}

function quoteDisplay(r: QuoteNumbering): { isInvoice: boolean; displayId: string } {
  const isInvoice = r.invoiceNumber != null;
  const pad = (n: number) => String(n).padStart(4, "0");
  const displayId = isInvoice
    ? `INV-${r.invoiceYear ?? r.quoteYear}-${pad(r.invoiceNumber as number)}`
    : `SO-${r.quoteYear}-${pad(r.quoteNumber)}`;
  return { isInvoice, displayId };
}

interface QuoteListResponse {
  id: string;
  quoteNumber: number;
  quoteYear: number;
  invoiceNumber: number | null;
  status: string;
  customerName: string;
  customerEmail: string | null;
  totalAmount: number;
  userId: string;
  creationTime: string;
  ownerName: string | null;
  customerCompany: string | null;
}

function toQuoteListItem(r: QuoteListResponse): QuoteListItem {
  const d = quoteDisplay({ ...r, invoiceYear: null });
  return {
    id: r.id,
    quoteNumber: r.quoteNumber,
    quoteYear: r.quoteYear,
    invoiceNumber: r.invoiceNumber ?? null,
    invoiceYear: null,
    displayId: d.displayId,
    isInvoice: d.isInvoice,
    status: r.status,
    customerName: r.customerName,
    customerCompany: r.customerCompany ?? null,
    totalAmount: r.totalAmount,
    currency: "USD",
    ownerId: r.userId,
    ownerName: r.ownerName ?? "",
    createdAt: r.creationTime,
    updatedAt: r.creationTime,
  };
}

function toQuoteDetail(r: Record<string, unknown>): QuoteDetail {
  const d = quoteDisplay(r as unknown as QuoteNumbering);
  return {
    ...(r as object),
    displayId: d.displayId,
    isInvoice: d.isInvoice,
    salesOrderId: (r.id as string) ?? "",
    invoiceId: null,
    ownerId: (r.userId as string) ?? "",
    ownerName: (r.ownerName as string) ?? "",
    campaignName: null,
    quoteNumber: (r.quoteNumber as number) ?? 0,
    quoteYear: (r.quoteYear as number) ?? 0,
    invoiceNumber: (r.invoiceNumber as number) ?? null,
    invoiceYear: (r.invoiceYear as number) ?? null,
    subtotal: (r.subtotal as number) ?? 0,
    discountPct: (r.discountPct as number) ?? 0,
    discountAmount: (r.discountAmount as number) ?? 0,
    taxRate: (r.taxRate as number) ?? 0,
    taxAmount: (r.taxAmount as number) ?? 0,
    shippingCharge: (r.shippingCharge as number) ?? 0,
    totalAmount: (r.totalAmount as number) ?? 0,
    priceLevelSnap: (r.priceLevelSnap as number) ?? null,
    items: (r.items as QuoteLineItem[]) ?? [],
    createdAt: (r.creationTime as string) ?? "",
    updatedAt: (r.creationTime as string) ?? "",
  } as unknown as QuoteDetail;
}

const QUOTE_SEARCH_COLS = ["customerName", "customerCompany", "customerEmail"];

function quoteTypeExpr(type?: "order" | "invoice"): string[] {
  if (type === "order") return [odata.eq("invoiceNumber", null)];
  if (type === "invoice") return [odata.ne("invoiceNumber", null)];
  return [];
}

export const quotes = {
  async list(
    params: PageParams & { type?: "order" | "invoice" } = {},
  ): Promise<Paged<QuoteListItem>> {
    const expr = [...(params.expr ?? []), ...quoteTypeExpr(params.type)];
    const res = await apiFetch<Paged<QuoteListResponse> | QuoteListResponse[]>(
      `/quotes${pageQuery({ ...params, expr }, undefined, QUOTE_SEARCH_COLS)}`,
    );
    const p = toPaged<QuoteListResponse>(res);
    return { ...p, items: p.items.map(toQuoteListItem) };
  },
  listAll(params: PageParams = {}): Promise<QuoteListItem[]> {
    return fetchAll((p) => this.list(p), params, "quotes");
  },
  async get(id: string) {
    const r = await apiFetch<Record<string, unknown>>(`/quotes/${encodeURIComponent(id)}`);
    return toQuoteDetail(r);
  },
  async create(req: SaveQuoteRequest) {
    const res = await apiFetch<unknown>("/quotes", {
      method: "POST",
      body: JSON.stringify(req),
    });
    if (typeof res === "string") return this.get(res);
    return toQuoteDetail((res ?? {}) as Record<string, unknown>);
  },
  update(id: string, req: SaveQuoteRequest) {
    return apiFetch<void>(`/quotes/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(req),
    });
  },
  remove(id: string) {
    return apiFetch<void>(`/quotes/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  async convertToInvoice(id: string) {
    await apiFetch<unknown>(`/quotes/${encodeURIComponent(id)}/convert-to-invoice`, {
      method: "POST",
    });
    return this.get(id);
  },
  async sendEmail(id: string) {
    await apiFetch<void>(`/quotes/${encodeURIComponent(id)}/send`, { method: "POST" });
    const detail = await this.get(id);
    return {
      sent: true,
      link: detail.publicToken
        ? url(`invoice-view/${detail.publicToken}`)
        : "",
    };
  },
  setStatus(id: string, status: QuoteStatusAction) {
    const path =
      status === "Sent"
        ? "send"
        : status === "Approved"
          ? "approve"
          : status === "Paid"
            ? "mark-paid"
            : "cancel";
    return apiFetch<void>(`/quotes/${encodeURIComponent(id)}/${path}`, {
      method: "POST",
    });
  },

  async viewPdf(id: string) {
    const blob = await apiDownload(`/quotes/${encodeURIComponent(id)}/pdf`);
    openBlobInNewTab(blob);
  },
};

export interface PublicInvoiceLine {
  description: string;
  unitPrice: number;
  quantity: number;
  lineTotal: number;
}

export interface PublicInvoice {
  displayId: string;
  status: string;
  issuedAt: string;
  customerName: string;
  customerCompany: string | null;
  customerEmail: string | null;
  customerAddress: string | null;
  notes: string | null;
  subtotal: number;
  discountPct: number;
  discountAmount: number;
  taxRate: number;
  taxAmount: number;
  shippingCharge: number;
  totalAmount: number;
  discountAppliesAfterTax: boolean;
  currency: string;
  companyName: string;
  companyAddress: string | null;
  companyEmail: string | null;
  companyPhone: string | null;
  items: PublicInvoiceLine[];
}

export const publicInvoices = {
  get(token: string) {
    return apiFetch<PublicInvoice>(`/public/invoices/${encodeURIComponent(token)}`);
  },

  pdfUrl(token: string) {
    return apiUrl(`/public/invoices/${encodeURIComponent(token)}/pdf`);
  },
};

export interface RecurringInvoiceListItem {
  id: string;
  profileName: string;
  customerName: string;
  customerCompany: string | null;
  frequency: string;
  interval: number;
  status: string;
  nextRunAt: string;
  lastRunAt: string | null;
  runsCompleted: number;
  ownerId: string;
  ownerName: string;
  updatedAt: string;
}

export interface RecurringInvoiceLine {
  id: string;
  productId: string | null;
  description: string;
  unitPrice: number;
  quantity: number;
  position: number;
}

export interface RecurringInvoiceDetail {
  id: string;
  profileName: string;
  ownerId: string;
  ownerName: string;
  frequency: string;
  interval: number;
  startDate: string;
  endDate: string | null;
  nextRunAt: string;
  lastRunAt: string | null;
  runsCompleted: number;
  status: string;
  emailSubject: string | null;
  orderNumber: string | null;
  trackingNumber: string | null;
  customerName: string;
  customerCompany: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  customerAddress: string | null;
  shippingName: string | null;
  shippingCompany: string | null;
  shippingEmail: string | null;
  shippingPhone: string | null;
  shippingAddress: string | null;
  discountPct: number;
  discountAmount: number;
  taxRate: number;
  shippingCharge: number;
  discountAppliesAfterTax: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  items: RecurringInvoiceLine[];
}

export interface RecurringInvoiceItemInput {
  productId?: string | null;
  description: string;
  unitPrice: number;
  quantity: number;
}

export interface SaveRecurringInvoiceRequest {
  profileName: string;
  frequency: "Day" | "Week" | "Month" | "Year";
  interval: number;
  startDate: string;
  endDate?: string | null;
  status?: "Active" | "Paused" | "Stopped";
  emailSubject?: string | null;
  orderNumber?: string | null;
  trackingNumber?: string | null;
  customerName: string;
  customerCompany?: string | null;
  customerEmail?: string | null;
  customerPhone?: string | null;
  customerAddress?: string | null;
  shippingName?: string | null;
  shippingCompany?: string | null;
  shippingEmail?: string | null;
  shippingPhone?: string | null;
  shippingAddress?: string | null;
  discountPct?: number;
  discountAmount?: number;
  taxRate?: number;
  shippingCharge?: number;
  discountAppliesAfterTax?: boolean | null;
  notes?: string | null;
  items: RecurringInvoiceItemInput[];
}

export const RECURRING_FREQUENCIES = ["Week", "Month", "Year"] as const;

export const RECURRING_STATUSES = ["Active", "Paused", "Stopped"] as const;

interface RecurringInvoiceListResponse {
  id: string;
  profileName: string;
  customerName: string;
  customerEmail: string | null;
  frequency: string;
  interval: number;
  status: string;
  nextRunAt: string;
  runsCompleted: number;
  creationTime: string;
  userId: string | null;
  ownerName: string | null;
  customerCompany: string | null;
  lastRunAt: string | null;
}

function toRecurringListItem(r: RecurringInvoiceListResponse): RecurringInvoiceListItem {
  return {
    id: r.id,
    profileName: r.profileName,
    customerName: r.customerName,
    customerCompany: r.customerCompany ?? null,
    frequency: r.frequency,
    interval: r.interval,
    status: r.status,
    nextRunAt: r.nextRunAt,
    lastRunAt: r.lastRunAt ?? null,
    runsCompleted: r.runsCompleted,
    ownerId: r.userId ?? "",
    ownerName: r.ownerName ?? "",
    updatedAt: r.creationTime,
  };
}

const RECURRING_SEARCH_COLS = ["profileName", "customerName", "customerCompany"];

export const recurringInvoices = {
  async list(params: PageParams = {}): Promise<Paged<RecurringInvoiceListItem>> {
    const res = await apiFetch<Paged<RecurringInvoiceListResponse> | RecurringInvoiceListResponse[]>(
      `/recurring-invoices${pageQuery(params, undefined, RECURRING_SEARCH_COLS)}`,
    );
    const p = toPaged<RecurringInvoiceListResponse>(res);
    return { ...p, items: p.items.map(toRecurringListItem) };
  },
  get(id: string) {
    return apiFetch<RecurringInvoiceDetail>(
      `/recurring-invoices/${encodeURIComponent(id)}`,
    );
  },
  create(req: SaveRecurringInvoiceRequest) {
    return apiFetch<IdResponse>("/recurring-invoices", {
      method: "POST",
      body: JSON.stringify(req),
    });
  },
  update(id: string, req: SaveRecurringInvoiceRequest) {
    return apiFetch<void>(`/recurring-invoices/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(req),
    });
  },
  remove(id: string) {
    return apiFetch<void>(`/recurring-invoices/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  },

  runNow(id: string) {
    return apiFetch<RecurringInvoiceRunNowResult>(
      `/recurring-invoices/${encodeURIComponent(id)}/run-now`,
      { method: "POST" },
    );
  },
};

export interface RecurringInvoiceRunNowResult {
  processedCount: number;
  failedCount: number;
  skippedCount: number;
  messages: string[];
}
